<template>
  <div class="provider-selector">
    <div class="form-group">
      <label v-if="showLabel" :for="inputId">
        {{ label }} <span v-if="required" class="required">*</span>
      </label>
      <input
        :id="inputId"
        type="text"
        v-model="providerSearchTerm"
        @input="handleProviderSearch"
        :placeholder="placeholder"
        class="form-input"
        :class="{ 'searching': isSearchingProviders }"
      />
      <small v-if="showHelpText" class="form-help">{{ helpText }}</small>
      
      <!-- Matching Providers -->
      <div v-if="providerSearchTerm && providerSearchTerm.trim().length >= 2 && !selectedProvider" class="provider-results">
        <div v-if="matchingProviders.length > 0" class="provider-results-header">
          Found {{ matchingProviders.length }} matching provider(s):
        </div>
        <div v-else-if="!isSearchingProviders" class="provider-results-header">
          No providers found
        </div>
        <div
          v-for="provider in matchingProviders"
          :key="provider?.id || Math.random()"
          class="provider-result-item"
          @click="selectProvider(provider)"
        >
          <div class="provider-name">{{ provider.name }}</div>
          <div class="provider-details">
            <span class="provider-type">{{ provider.provider_type }}</span>
            <span v-if="provider?.city && provider?.state" class="provider-location">
              • {{ provider.city }}, {{ provider.state }}
            </span>
          </div>
        </div>
        <div v-if="!isSearchingProviders && allowCreation" class="provider-results-footer">
          <button type="button" @click="handleCreateNewProvider" class="btn-link">
            Not found? Create a new provider
          </button>
        </div>
      </div>
      
      <!-- Selected Provider Display -->
      <div v-if="selectedProvider" class="selected-provider">
        <div class="selected-provider-info">
          <strong>{{ selectedProvider.name }}</strong>
          <span class="provider-type-badge">{{ selectedProvider.provider_type }}</span>
        </div>
        <button type="button" @click="clearProviderSelection" class="btn-link-small">
          Change provider
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { searchProviders } from '../../services/providerService.js'

export default {
  name: 'ProviderSelector',
  props: {
    label: {
      type: String,
      default: 'Provider'
    },
    required: {
      type: Boolean,
      default: false
    },
    initialValue: {
      type: Object,
      default: null
    },
    allowCreation: {
      type: Boolean,
      default: true
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    requirePostcode: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Search for a provider by name'
    },
    helpText: {
      type: String,
      default: 'Start typing to search for an existing provider'
    },
    showHelpText: {
      type: Boolean,
      default: true
    },
    inputId: {
      type: String,
      default: 'providerSearch'
    }
  },
  emits: ['provider-selected', 'provider-cleared', 'provider-ready', 'create-new-provider'],
  data() {
    return {
      providerSearchTerm: '',
      matchingProviders: [],
      selectedProvider: null,
      isSearchingProviders: false,
      searchTimeout: null
    }
  },
  watch: {
    initialValue(newVal) {
      if (newVal) {
        this.selectedProvider = newVal
        this.providerSearchTerm = newVal.name
      } else {
        this.clearProviderSelection()
      }
    }
  },
  mounted() {
    if (this.initialValue) {
      this.selectedProvider = this.initialValue
      this.providerSearchTerm = this.initialValue.name
    }
  },
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    async handleProviderSearch() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Clear selection if search term changes
      if (this.selectedProvider && this.providerSearchTerm !== this.selectedProvider.name) {
        this.selectedProvider = null
        this.$emit('provider-cleared')
      }

      // Don't search if term is too short
      if (this.providerSearchTerm.trim().length < 2) {
        this.matchingProviders = []
        return
      }

      // Debounce search
      this.searchTimeout = setTimeout(async () => {
        this.isSearchingProviders = true
        try {
          const providers = await searchProviders(this.providerSearchTerm)
          this.matchingProviders = providers

          console.log('matchingProviders', this.matchingProviders)
          console.log('matchingProviders length', this.matchingProviders.length)
          
          // If exactly one match and it matches the search term exactly, auto-select it
          if (providers.length === 1 && 
              providers[0].name.toLowerCase() === this.providerSearchTerm.trim().toLowerCase()) {
            this.selectProvider(providers[0])
          }
        } catch (error) {
          console.error('Error searching providers:', error)
          this.matchingProviders = []
        } finally {
          this.isSearchingProviders = false
        }
      }, 300)
    },
    selectProvider(provider) {
      this.selectedProvider = provider
      this.providerSearchTerm = provider.name
      this.matchingProviders = []
      this.$emit('provider-selected', provider)
      this.$emit('provider-ready', provider)
    },
    clearProviderSelection() {
      this.selectedProvider = null
      this.providerSearchTerm = ''
      this.matchingProviders = []
      this.$emit('provider-cleared')
    },
    handleCreateNewProvider() {
      // Emit event with the current search term so parent can populate the form
      this.$emit('create-new-provider', this.providerSearchTerm.trim())
    },
    // Public method to get the selected provider
    async getProvider() {
      return this.selectedProvider
    },
    // Public method to check if provider is ready (selected)
    isProviderReady() {
      return !!this.selectedProvider
    },
    // Public method to reset the component
    reset() {
      this.clearProviderSelection()
    }
  }
}
</script>

<style scoped>
.provider-selector {
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-help {
  display: block;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  margin-top: 4px;
}

.provider-results {
  margin-top: 12px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid var(--color-border-main);
  border-radius: 8px;
  background: var(--color-surface-light);
}

.provider-results-header {
  padding: 12px 16px;
  background: var(--color-surface-dark);
  font-weight: 600;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-border-light);
}

.provider-result-item {
  padding: 16px;
  border-bottom: 1px solid var(--color-border-light);
  cursor: pointer;
  transition: all var(--transition-normal);
}

.provider-result-item:last-child {
  border-bottom: none;
}

.provider-result-item:hover {
  background: var(--color-bg-main);
}

.provider-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.provider-details {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.provider-type {
  font-weight: 500;
}

.provider-location {
  color: var(--color-text-light);
}

.provider-results-footer {
  padding: 12px 16px;
  text-align: center;
  border-top: 1px solid var(--color-border-light);
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.9rem;
}

.btn-link:hover {
  color: var(--color-primary-dark);
}

.selected-provider {
  margin-top: 12px;
  padding: 16px;
  background: var(--color-surface-elevated);
  border: 2px solid var(--color-primary-main);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.selected-provider-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.provider-type-badge {
  background: var(--color-primary-light);
  color: var(--color-primary-main);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.btn-link-small {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.85rem;
}

.btn-link-small:hover {
  color: var(--color-primary-dark);
}

.form-input.searching {
  border-color: var(--color-primary-main);
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .selected-provider {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

